import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Toast from "@moltech/molui/toast";
import { usePostDataMutation } from "../../app/services/commonApi";
import {
  getAiApiMessage,
  isAiApiSuccess,
  formatAiErrorMessage,
} from "../../pages/aiPlatform/utils/aiApiResponse";
import { useCopilotShell } from "./CopilotShellContext";
import {
  buildChatContext,
  draftPromptFromSuggestion,
  hasUnresolvedEntityToken,
  isCompleteSuggestion,
  isImmediatePrompt,
  normalizeAssistantMessage,
  pick,
  resolveActiveListSession,
  resolveActiveWorkflow,
} from "./copilotUtils";

export const failedAssistantMessage = (errorText) => ({
  role: "assistant",
  content: "Something went wrong. Try again.",
  errorText: errorText || "MolAssist failed.",
  isError: true,
  toolsRan: [],
  suggestedActions: [],
  toolResults: [],
  blocks: [],
  draftEmailText: null,
  draftEmailSubject: null,
  draftEmailRecipients: [],
  workflow: null,
  listSession: null,
  pendingEntityQuery: null,
  lastListFilters: null,
  lastMetricCode: null,
  pendingConfirmation: null,
  aiExecutionLogId: null,
});

const isClearChatMessage = (message) => /^clear(\s+chat)?$/i.test(message);

const focusComposer = () => {
  requestAnimationFrame(() => {
    document.querySelector(".copilot-composer__input")?.focus?.();
  });
};

const getRecentHistory = (messages) =>
  messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .slice(-8)
    .map((m) => ({ role: m.role, content: m.content }));

const replaceLatestAssistantMessage = (messages, assistantMsg) => {
  const nextMessages = [...messages];
  const latestAssistantIndex = nextMessages.findLastIndex(
    (message) => message.role === "assistant"
  );

  if (latestAssistantIndex >= 0) {
    nextMessages[latestAssistantIndex] = {
      ...nextMessages[latestAssistantIndex],
      ...assistantMsg,
    };
  }

  return nextMessages;
};

const getWorkflowState = (workflowOverride, currentThread) =>
  workflowOverride !== undefined
    ? workflowOverride
    : resolveActiveWorkflow(currentThread);

const buildAssistantChatPayload = ({
  current,
  message,
  nextMessages,
  workflowOverride,
  pageContext,
  jobOpeningIdParam,
}) => {
  const context = buildChatContext({
    pageContext: current.pageContext || pageContext,
    workflow: getWorkflowState(workflowOverride, current),
    attachments: current.attachments,
    jobOpeningIdParam,
    listSession: resolveActiveListSession(current),
    pendingEntityQuery: current.pendingEntityQuery,
    lastListFilters: current.lastListFilters,
    lastMetricCode: current.lastMetricCode,
  });

  return {
    conversationId: current.conversationId,
    message,
    history: getRecentHistory(nextMessages),
    enableTools: true,
    context,
  };
};

const appendFailureMessage = ({
  quiet,
  current,
  nextMessages,
  errorText,
  setThreadMessages,
}) => {
  if (quiet) return;

  setThreadMessages(current.id, [
    ...nextMessages,
    failedAssistantMessage(errorText),
  ]);
};

export const useCopilotChatSession = ({
  jobOpeningIdProp,
  isUploading,
  input,
  setInput,
}) => {
  const [searchParams] = useSearchParams();
  const { pageContext, threads: threadApi } = useCopilotShell();
  const [postData, { isLoading }] = usePostDataMutation();
  const [isSendingEmailDraft, setIsSendingEmailDraft] = useState(false);

  const jobOpeningIdParam = jobOpeningIdProp ?? searchParams.get("jobOpeningId");
  const deepLinkQuery = searchParams.get("q");

  const {
    activeThread,
    ensureActiveThread,
    setThreadMessages,
    setThreadWorkflow,
    setThreadEntityEcho,
    setThreadConversationId,
    maybeSetThreadTitle,
    clearAllThreads,
  } = threadApi;

  const thread = activeThread || ensureActiveThread();
  const messages = thread?.messages || [];
  const workflow = thread?.workflow || null;

  const fillComposer = useCallback((text) => {
    setInput(draftPromptFromSuggestion(text));
    focusComposer();
  }, [setInput]);

  const send = useCallback(
    async (rawMessage, workflowOverride, options = {}) => {
      const message = String(rawMessage || "").trim();
      if (isClearChatMessage(message)) {
        clearAllThreads();
        setInput("");
        Toast.success("Chat cleared.");
        return;
      }
      if (!message || isLoading || isUploading) return;
      if (hasUnresolvedEntityToken(message)) {
        setInput(message);
        Toast.info("Pick an item from the @ dropdown before sending.");
        focusComposer();
        return;
      }

      const current = ensureActiveThread();
      const quiet = Boolean(options.quiet);
      const userCount = current.messages.filter((m) => m.role === "user").length;
      if (!quiet && userCount === 0) maybeSetThreadTitle(current.id, message);

      const nextMessages = quiet
        ? current.messages
        : [...current.messages, { role: "user", content: message }];
      if (!quiet) {
        setThreadMessages(current.id, nextMessages);
        setInput("");
      }

      try {
        const payload = buildAssistantChatPayload({
          current,
          message,
          nextMessages,
          workflowOverride,
          pageContext,
          jobOpeningIdParam,
        });

        const response = await postData({
          url: "/AIExecution/AssistantChat",
          data: payload,
        }).unwrap();

        if (!isAiApiSuccess(response)) {
          const errorText = formatAiErrorMessage(
            getAiApiMessage(response),
            "MolAssist request failed."
          );
          Toast.error(errorText);
          appendFailureMessage({
            quiet,
            current,
            nextMessages,
            errorText,
            setThreadMessages,
          });
          return;
        }

        const data = pick(response, "data", "Data") || response;
        const assistantMsg = normalizeAssistantMessage(data);
        const nextConversationId =
          pick(data, "conversationId", "ConversationId") || current.conversationId;

        setThreadConversationId(current.id, nextConversationId);
        setThreadWorkflow(current.id, assistantMsg.workflow);
        setThreadEntityEcho(current.id, {
          listSession: assistantMsg.listSession,
          pendingEntityQuery: assistantMsg.pendingEntityQuery,
          lastListFilters: assistantMsg.lastListFilters,
          lastMetricCode: assistantMsg.lastMetricCode,
        });

        if (quiet) {
          setThreadMessages(
            current.id,
            replaceLatestAssistantMessage(current.messages, assistantMsg)
          );
        } else {
          setThreadMessages(current.id, [...nextMessages, assistantMsg]);
        }
      } catch (err) {
        const errorText = formatAiErrorMessage(
          err?.data?.message || err?.data?.Message || err?.message,
          "MolAssist failed."
        );
        Toast.error(errorText);
        appendFailureMessage({
          quiet,
          current,
          nextMessages,
          errorText,
          setThreadMessages,
        });
      }
    },
    [
      clearAllThreads,
      ensureActiveThread,
      isLoading,
      isUploading,
      jobOpeningIdParam,
      maybeSetThreadTitle,
      pageContext,
      postData,
      setInput,
      setThreadConversationId,
      setThreadEntityEcho,
      setThreadMessages,
      setThreadWorkflow,
    ]
  );

  const runSuggestion = useCallback(
    (text) => {
      const drafted = draftPromptFromSuggestion(text);
      if (
        isImmediatePrompt(text) ||
        isImmediatePrompt(drafted) ||
        isCompleteSuggestion(text)
      ) {
        send(drafted || text);
      } else {
        fillComposer(text);
      }
    },
    [fillComposer, send]
  );

  const onSendEmailDraft = useCallback(
    async (draft) => {
      const subject = draft?.subject || "";
      const recipients = Array.isArray(draft?.recipients) ? draft.recipients : [];

      if (!subject || recipients.length === 0) return;

      setIsSendingEmailDraft(true);
      try {
        const result = await postData({
          url: "/AIExecution/SendCopilotShortlistEmail",
          data: {
            subject,
            recipients: recipients.map((r) => ({
              applicantId: r.applicantId ?? null,
              applicantName: r.applicantName ?? null,
              applicantEmail: r.applicantEmail ?? null,
              jobOpeningName: r.jobOpeningName ?? null,
              body: r.body ?? null,
            })),
          },
        }).unwrap();

        if (result) {
          Toast.success("Emails sent successfully.");
        } else {
          Toast.error("Failed to send emails.");
        }
      } catch (err) {
        Toast.error(err?.data?.message || err?.message || "Failed to send emails.");
        throw err;
      } finally {
        setIsSendingEmailDraft(false);
      }
    },
    [postData]
  );

  useEffect(() => {
    if (!deepLinkQuery) return;
    send(deepLinkQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount deep-link only
  }, []);

  return {
    thread,
    messages,
    workflow,
    isLoading,
    isSendingEmailDraft,
    jobOpeningIdParam,
    pageContext,
    threadApi,
    send,
    runSuggestion,
    fillComposer,
    onSendEmailDraft,
  };
};
